import { 
  collection, 
  doc, 
  getDocs, 
  getDocsFromServer,
  setDoc, 
  updateDoc, 
  serverTimestamp,
  deleteField
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { 
  ProjectAssignmentConsolidated, 
  ProjectAssignmentInfo, 
  ProjectSubTaskInfo,
  ProjectSubTaskStudentStatus,
  AssignmentStatusValue,
  ProjectAssignmentConsolidatedV2,
  ProjectAssignmentInfoV2,
  ProjectSubTaskInfoV2,
  ProjectSubTaskStudentStatusV2
} from '../types/index';
import { ProjectAssignment, SubTask } from '../types';
import { normalizeMemberGroup, withMemberGroup } from '../utils/memberGroupPaths';
import { assignmentSubmissionImageService } from './assignmentSubmissionImageService';

// ===== 배열 ↔ 중첩 객체 변환 유틸리티 =====

function studentStatusesArrayToObjectProject(
  arr: ProjectSubTaskStudentStatus[]
): { [studentId: string]: ProjectSubTaskStudentStatusV2 } {
  const obj: { [studentId: string]: ProjectSubTaskStudentStatusV2 } = {};
  for (const status of arr) {
    const { studentId, ...rest } = status;
    obj[studentId] = rest;
  }
  return obj;
}

function studentStatusesObjectToArrayProject(
  obj: { [studentId: string]: ProjectSubTaskStudentStatusV2 }
): ProjectSubTaskStudentStatus[] {
  return Object.entries(obj).map(([studentId, status]) => ({
    studentId,
    ...status
  }));
}

function subTasksArrayToObject(
  arr: ProjectSubTaskInfo[]
): { [subTaskId: string]: ProjectSubTaskInfoV2 } {
  const obj: { [subTaskId: string]: ProjectSubTaskInfoV2 } = {};
  for (const subTask of arr) {
    const { subTaskId, studentStatuses, ...rest } = subTask;
    obj[subTaskId] = {
      ...rest,
      studentStatuses: studentStatusesArrayToObjectProject(studentStatuses)
    };
  }
  return obj;
}

function subTasksObjectToArray(
  obj: { [subTaskId: string]: ProjectSubTaskInfoV2 }
): ProjectSubTaskInfo[] {
  const arr = Object.entries(obj).map(([subTaskId, subTask]) => ({
    subTaskId,
    ...subTask,
    studentStatuses: studentStatusesObjectToArrayProject(subTask.studentStatuses)
  }));
  // Firestore/객체에서 읽을 때 키 순서가 보장되지 않으므로 subTaskOrder로 정렬해 순서 복원
  arr.sort((a, b) => (a.subTaskOrder ?? 0) - (b.subTaskOrder ?? 0));
  return arr;
}

function assignmentsArrayToObjectProject(
  arr: ProjectAssignmentInfo[]
): { [assignmentId: string]: ProjectAssignmentInfoV2 } {
  const obj: { [assignmentId: string]: ProjectAssignmentInfoV2 } = {};
  for (const assignment of arr) {
    const { assignmentId, subTasks, ...rest } = assignment;
    obj[assignmentId] = {
      ...rest,
      subTasks: subTasksArrayToObject(subTasks)
    };
  }
  return obj;
}

function assignmentsObjectToArrayProject(
  obj: { [assignmentId: string]: ProjectAssignmentInfoV2 }
): ProjectAssignmentInfo[] {
  return Object.entries(obj).map(([assignmentId, assignment]) => ({
    assignmentId,
    ...assignment,
    subTasks: subTasksObjectToArray(assignment.subTasks)
  }));
}

function isArrayData(data: any): boolean {
  return Array.isArray(data);
}

const PROJECT_CACHE_PREFIX = 'pa_';

export class ProjectAssignmentConsolidatedService {
  private userId: string;
  private classId: string;
  private maxDocumentSize: number = 1024 * 1024; // 1MB
  private memberGroup: string;
  private collectionName: string;
  private useNestedObjects: boolean = true; // V2 중첩 객체 사용 여부

  private static __consolidatedCache = new Map<string, { v1: ProjectAssignmentConsolidated[]; v2: ProjectAssignmentConsolidatedV2[] }>();
  /** 캐시 무효화된 키: 다음 읽기 시 서버에서 강제 로드해 방금 쓴 값이 반영되도록 함 */
  private static __invalidatedKeys = new Set<string>();

  private getCacheKey(): string {
    return `${PROJECT_CACHE_PREFIX}${this.userId}_${this.classId}_${this.collectionName}`;
  }

  private invalidateConsolidatedCache(): void {
    const key = this.getCacheKey();
    ProjectAssignmentConsolidatedService.__consolidatedCache.delete(key);
    ProjectAssignmentConsolidatedService.__invalidatedKeys.add(key);
  }

  constructor(userId: string, classId: string, memberGroup?: string) {
    this.userId = userId;
    this.classId = classId;
    this.memberGroup = normalizeMemberGroup(memberGroup);
    this.collectionName = withMemberGroup('projectAssignmentStatuses', this.memberGroup);
  }

  /**
   * 현재 문서 번호 조회
   */
  private async getCurrentDocumentNumber(): Promise<number> {
    const consolidatedRef = collection(
      db, 
      'users', 
      this.userId, 
      'classes', 
      this.classId, 
      this.collectionName
    );
    const snapshot = await getDocs(consolidatedRef);
    return snapshot.docs.length;
  }

  /**
   * 모든 통합 문서 조회 (캐시 사용; 쓰기 시 무효화)
   */
  public async getAllConsolidatedDocuments(): Promise<ProjectAssignmentConsolidated[]> {
    const key = this.getCacheKey();
    const cached = ProjectAssignmentConsolidatedService.__consolidatedCache.get(key);
    if (cached) return cached.v1;

    const consolidatedRef = collection(
      db, 'users', this.userId, 'classes', this.classId, this.collectionName
    );
    const useServer = ProjectAssignmentConsolidatedService.__invalidatedKeys.has(key);
    if (useServer) ProjectAssignmentConsolidatedService.__invalidatedKeys.delete(key);
    const snapshot = useServer ? await getDocsFromServer(consolidatedRef) : await getDocs(consolidatedRef);
    const v1 = snapshot.docs.map(docSnap => {
      const data = docSnap.data();
      let assignmentsArray: ProjectAssignmentInfo[] = [];
      if (data.assignments) {
        if (isArrayData(data.assignments)) assignmentsArray = data.assignments;
        else assignmentsArray = assignmentsObjectToArrayProject(data.assignments);
      }
      return {
        ...data,
        id: docSnap.id,
        lastUpdated: data.lastUpdated?.toDate() || new Date(),
        createdAt: data.createdAt?.toDate() || new Date(),
        assignments: assignmentsArray
      };
    }) as ProjectAssignmentConsolidated[];
    const v2 = snapshot.docs.map(docSnap => {
      const data = docSnap.data();
      let assignmentsObj: { [assignmentId: string]: ProjectAssignmentInfoV2 } = {};
      if (data.assignments) {
        if (isArrayData(data.assignments)) assignmentsObj = assignmentsArrayToObjectProject(data.assignments);
        else assignmentsObj = data.assignments;
      }
      return {
        ...data,
        id: docSnap.id,
        lastUpdated: data.lastUpdated?.toDate() || new Date(),
        createdAt: data.createdAt?.toDate() || new Date(),
        assignments: assignmentsObj
      };
    }) as ProjectAssignmentConsolidatedV2[];
    ProjectAssignmentConsolidatedService.__consolidatedCache.set(key, { v1, v2 });
    return v1;
  }

  /**
   * 모든 통합 문서 조회 V2 (캐시 사용; 쓰기 시 무효화)
   */
  public async getAllConsolidatedDocumentsV2(): Promise<ProjectAssignmentConsolidatedV2[]> {
    const key = this.getCacheKey();
    const cached = ProjectAssignmentConsolidatedService.__consolidatedCache.get(key);
    if (cached) return cached.v2;

    const consolidatedRef = collection(
      db, 'users', this.userId, 'classes', this.classId, this.collectionName
    );
    const useServer = ProjectAssignmentConsolidatedService.__invalidatedKeys.has(key);
    if (useServer) ProjectAssignmentConsolidatedService.__invalidatedKeys.delete(key);
    const snapshot = useServer ? await getDocsFromServer(consolidatedRef) : await getDocs(consolidatedRef);
    const v1 = snapshot.docs.map(docSnap => {
      const data = docSnap.data();
      let assignmentsArray: ProjectAssignmentInfo[] = [];
      if (data.assignments) {
        if (isArrayData(data.assignments)) assignmentsArray = data.assignments;
        else assignmentsArray = assignmentsObjectToArrayProject(data.assignments);
      }
      return {
        ...data,
        id: docSnap.id,
        lastUpdated: data.lastUpdated?.toDate() || new Date(),
        createdAt: data.createdAt?.toDate() || new Date(),
        assignments: assignmentsArray
      };
    }) as ProjectAssignmentConsolidated[];
    const v2 = snapshot.docs.map(docSnap => {
      const data = docSnap.data();
      let assignmentsObj: { [assignmentId: string]: ProjectAssignmentInfoV2 } = {};
      if (data.assignments) {
        if (isArrayData(data.assignments)) assignmentsObj = assignmentsArrayToObjectProject(data.assignments);
        else assignmentsObj = data.assignments;
      }
      return {
        ...data,
        id: docSnap.id,
        lastUpdated: data.lastUpdated?.toDate() || new Date(),
        createdAt: data.createdAt?.toDate() || new Date(),
        assignments: assignmentsObj
      };
    }) as ProjectAssignmentConsolidatedV2[];
    ProjectAssignmentConsolidatedService.__consolidatedCache.set(key, { v1, v2 });
    return v2;
  }

  /**
   * 문서 크기 계산
   */
  private calculateDocumentSize(doc: ProjectAssignmentConsolidated): number {
    return JSON.stringify(doc).length;
  }

  /**
   * 새로운 통합 문서 생성 (V2: 중첩 객체 형식)
   */
  private async createNewConsolidatedDocument(
    documentNumber: number, 
    data: Partial<ProjectAssignmentConsolidated> | { assignments: { [key: string]: ProjectAssignmentInfoV2 } }
  ): Promise<string> {
    const docId = `projectAssignment-${documentNumber}`;
    const docRef = doc(
      db, 
      'users', 
      this.userId, 
      'classes', 
      this.classId, 
      this.collectionName, 
      docId
    );
    
    // V2: 중첩 객체로 저장
    const consolidatedDoc = {
      id: docId,
      classId: this.classId,
      lastUpdated: new Date(),
      documentNumber,
      assignments: this.useNestedObjects ? {} : [],
      createdAt: new Date(),
      totalSize: 0,
      ...data
    };
    
    await setDoc(docRef, {
      ...consolidatedDoc,
      lastUpdated: serverTimestamp(),
      createdAt: serverTimestamp()
    });
    this.invalidateConsolidatedCache();
    return docId;
  }

  /**
   * 프로젝트 과제 생성 (V2: 중첩 객체 형식)
   */
  async createProjectAssignment(
    projectAssignment: ProjectAssignment,
    subTasks: SubTask[]
  ): Promise<string> {
    const assignmentId = projectAssignment.id;
    
    // V2: 하위 과제 정보 생성 (중첩 객체 형식)
    const subTaskInfosV2: { [subTaskId: string]: ProjectSubTaskInfoV2 } = {};
    for (const subTask of subTasks) {
      // 각 하위 과제에 대해 부여받은 모든 멤버들의 초기 상태 생성 (중첩 객체)
      const initialStudentStatusesV2: { [studentId: string]: ProjectSubTaskStudentStatusV2 } = {};
      for (const studentId of projectAssignment.assignedStudentIds) {
        initialStudentStatusesV2[studentId] = {
          status: 'pending' as AssignmentStatusValue,
          earnedPoints: 0,
          lastUpdated: new Date()
        };
      }

      subTaskInfosV2[subTask.id] = {
        subTaskOrder: subTask.order,
        subTaskBasePoints: subTask.points,
        subTaskTitle: subTask.title,
        studentStatuses: initialStudentStatusesV2
      };
    }

    // V2: 프로젝트 과제 정보 생성 (중첩 객체)
    const assignmentInfoV2: ProjectAssignmentInfoV2 = {
      title: projectAssignment.title,
      description: projectAssignment.description || '',
      dueDate: projectAssignment.dueDate.toISOString().split('T')[0],
      isActive: projectAssignment.isActive !== undefined ? projectAssignment.isActive : true,
      assignedStudentIds: projectAssignment.assignedStudentIds,
      classId: this.classId,
      color: projectAssignment.color || '#FC5555',
      createdAt: new Date(),
      updatedAt: new Date(),
      subTasks: subTaskInfosV2
    };

    // 새로운 프로젝트 보상 필드
    if (projectAssignment.rewardEnabled !== undefined) {
      assignmentInfoV2.rewardEnabled = projectAssignment.rewardEnabled;
    }
    if (projectAssignment.rewardMessage && projectAssignment.rewardMessage.trim()) {
      assignmentInfoV2.rewardMessage = projectAssignment.rewardMessage;
    }
    if (projectAssignment.rewardCoins !== undefined && projectAssignment.rewardCoins > 0) {
      assignmentInfoV2.rewardCoins = projectAssignment.rewardCoins;
    }
    if (projectAssignment.rewardItemCardIds && projectAssignment.rewardItemCardIds.length > 0) {
      assignmentInfoV2.rewardItemCardIds = projectAssignment.rewardItemCardIds;
    }

    // V2: 기존 통합 문서들 조회 (중첩 객체 형식)
    const consolidatedDocsV2 = await this.getAllConsolidatedDocumentsV2();
    
    if (consolidatedDocsV2.length === 0) {
      // 첫 번째 문서 생성 (V2 중첩 객체 형식)
      const documentNumber = await this.getCurrentDocumentNumber() + 1;
      const docId = await this.createNewConsolidatedDocument(documentNumber, {
        assignments: { [assignmentId]: assignmentInfoV2 }
      });
      
      console.log('✅ 첫 번째 프로젝트 과제 통합 문서 생성 완료 (V2):', {
        assignmentId,
        docId,
        assignmentCount: 1,
        subTaskCount: subTasks.length,
        studentCount: projectAssignment.assignedStudentIds.length
      });
      
      return docId;
    }

    // 마지막 문서에 과제 추가
    const lastDocV2 = consolidatedDocsV2[consolidatedDocsV2.length - 1];
    lastDocV2.assignments[assignmentId] = assignmentInfoV2;
    
    const updatedSize = JSON.stringify(lastDocV2).length;

    if (updatedSize > this.maxDocumentSize) {
      // 문서 분할 필요 - 새 문서 생성
      const documentNumber = await this.getCurrentDocumentNumber() + 1;
      const docId = await this.createNewConsolidatedDocument(documentNumber, {
        assignments: { [assignmentId]: assignmentInfoV2 }
      });
      return docId;
    }

    // V2: 부분 업데이트 (해당 과제만 추가)
    const docRef = doc(
      db, 
      'users', 
      this.userId, 
      'classes', 
      this.classId, 
      this.collectionName, 
      lastDocV2.id
    );
    
    await updateDoc(docRef, {
      [`assignments.${assignmentId}`]: assignmentInfoV2,
      totalSize: updatedSize,
      lastUpdated: serverTimestamp()
    });
    this.invalidateConsolidatedCache();

    console.log('✅ 프로젝트 과제 통합 문서에 과제 추가 완료 (V2):', {
      assignmentId,
      documentId: lastDocV2.id,
      assignmentCount: Object.keys(lastDocV2.assignments).length,
      subTaskCount: subTasks.length,
      studentCount: projectAssignment.assignedStudentIds.length
    });

    return lastDocV2.id;
  }

  /**
   * 하위 과제 멤버 상태 업데이트 (V2: 부분 업데이트 - dot notation)
   */
  async updateSubTaskStudentStatus(
    assignmentId: string,
    subTaskId: string,
    studentId: string,
    status: AssignmentStatusValue,
    earnedPoints: number,
    memo: string = ''
  ): Promise<void> {
    try {
      // V2: 중첩 객체 형식으로 문서 조회
      const consolidatedDocsV2 = await this.getAllConsolidatedDocumentsV2();
      
      let targetDocV2: ProjectAssignmentConsolidatedV2 | null = null;
      let targetAssignmentV2: ProjectAssignmentInfoV2 | null = null;
      let targetSubTaskV2: ProjectSubTaskInfoV2 | null = null;
      
      for (const docV2 of consolidatedDocsV2) {
        if (docV2.assignments[assignmentId]) {
          const assignment = docV2.assignments[assignmentId];
          if (assignment.subTasks[subTaskId]) {
            targetDocV2 = docV2;
            targetAssignmentV2 = assignment;
            targetSubTaskV2 = assignment.subTasks[subTaskId];
            break;
          }
        }
      }

      if (!targetDocV2 || !targetAssignmentV2 || !targetSubTaskV2) {
        throw new Error(`프로젝트 과제 ${assignmentId}의 하위 과제 ${subTaskId}를 찾을 수 없습니다.`);
      }

      // 이전 상태 확인 (보상 처리용)
      const existingStatus = targetSubTaskV2.studentStatuses?.[studentId];
      const oldStatus = existingStatus?.status || 'pending';
      
      // 완료 상태 여부 확인
      const isCompletedStatus = (s: AssignmentStatusValue) => 
        ['confirmed', 'delayed', 'exempted'].includes(s);
      const wasCompleted = isCompletedStatus(oldStatus);
      const isNowCompleted = isCompletedStatus(status);

      // V2: 새 학생 상태 (중첩 객체)
      const studentStatusV2: ProjectSubTaskStudentStatusV2 = {
        status,
        earnedPoints,
        memo,
        lastUpdated: new Date()
      };

      // V2: 부분 업데이트 (해당 학생만) - dot notation 사용
      const docRef = doc(
        db, 
        'users', 
        this.userId, 
        'classes', 
        this.classId, 
        this.collectionName, 
        targetDocV2.id
      );
      
      await updateDoc(docRef, {
        [`assignments.${assignmentId}.subTasks.${subTaskId}.studentStatuses.${studentId}`]: studentStatusV2,
        [`assignments.${assignmentId}.updatedAt`]: new Date(),
        lastUpdated: serverTimestamp()
      });
      this.invalidateConsolidatedCache();

      // 보상 처리 (하위 과제 상태 변경 시 프로젝트 전체 완료 여부 확인)
      if (wasCompleted !== isNowCompleted) {
        // 배열 형식으로 변환하여 보상 처리 (기존 로직 호환)
        const targetAssignment: ProjectAssignmentInfo = {
          assignmentId,
          ...targetAssignmentV2,
          subTasks: subTasksObjectToArray(targetAssignmentV2.subTasks || {})
        };
        
        await this.processProjectAssignmentRewards(
          assignmentId,
          studentId,
          targetAssignment,
          wasCompleted,
          isNowCompleted
        );
      }

      console.log('✅ 프로젝트 과제 하위 과제 멤버 상태 업데이트 완료 (V2 부분 업데이트):', {
        assignmentId,
        subTaskId,
        studentId,
        status,
        earnedPoints,
        memo
      });

    } catch (error) {
      console.error('❌ 프로젝트 과제 하위 과제 멤버 상태 업데이트 실패:', error);
      throw error;
    }
  }

  /**
   * 프로젝트 과제 완료 보상 처리 (자동)
   * 모든 하위 과제가 완료되어야 보상 지급
   */
  private async processProjectAssignmentRewards(
    assignmentId: string,
    studentId: string,
    assignment: ProjectAssignmentInfo,
    wasSubTaskCompleted: boolean,
    isSubTaskNowCompleted: boolean
  ): Promise<void> {
    try {
      // 보상이 활성화되어 있지 않으면 처리하지 않음
      if (!assignment.rewardEnabled) {
        return;
      }

      const hasCoins = assignment.rewardCoins && assignment.rewardCoins > 0;
      const hasItemCards = assignment.rewardItemCardIds && assignment.rewardItemCardIds.length > 0;

      if (!hasCoins && !hasItemCards) {
        return;
      }

      // 프로젝트 전체 완료 여부 확인 (모든 하위 과제가 완료되어야 함)
      const isProjectCompleted = assignment.subTasks.every(subTask => {
        const studentStatus = subTask.studentStatuses.find(ss => ss.studentId === studentId);
        if (!studentStatus) return false;
        return ['confirmed', 'delayed', 'exempted'].includes(studentStatus.status);
      });

      // 동적 import로 순환 참조 방지
      const { ItemCardService } = await import('./itemCardService');
      const { StudentCoinService } = await import('./studentCoinService');
      
      const itemCardService = new ItemCardService(this.userId, this.classId);
      const coinService = new StudentCoinService(this.userId, this.classId);

      // 이미 보상을 받았는지 확인
      const alreadyGranted = assignment.rewardGrantedStudentIds?.includes(studentId);

      if (isProjectCompleted && !alreadyGranted) {
        // 프로젝트 완료: 보상 지급
        console.log(`🎁 프로젝트 과제 완료 보상 지급 시작: ${assignmentId} - ${studentId}`);
        
        // 코인 지급
        if (hasCoins) {
          await coinService.grantCoinsToStudent(
            studentId,
            assignment.rewardCoins!,
            `${assignment.title} 프로젝트 과제 완료 보상`,
            'General'
          );
          console.log(`💰 코인 지급: ${assignment.rewardCoins}코인`);
        }

        // 아이템 카드 지급
        if (hasItemCards) {
          const itemCardSet = await itemCardService.loadItemCardSet();
          if (itemCardSet) {
            const rewardItemCards = (assignment.rewardItemCardIds || [])
              .map(itemCardId => itemCardSet.assignmentItem.find(item => item.itemCardId === itemCardId))
              .filter((item): item is any => item !== undefined);

            const studentItemCards = await itemCardService.getStudentItemCards(studentId);
            const existingRewards = studentItemCards.filter(card => card.assignmentId === assignmentId);

            if (existingRewards.length > 0) {
              await itemCardService.unlockStudentItemCardsByAssignment(studentId, assignmentId);
            }

            const newRewards = rewardItemCards.filter(rewardItem =>
              !existingRewards.some(existing => existing.itemCardId === rewardItem.itemCardId)
            );

            for (const rewardItem of newRewards) {
              const studentItemCard: any = {
                instanceId: `project_${assignmentId}_${rewardItem.itemCardId}_${Date.now()}`,
                itemCardId: rewardItem.itemCardId,
                type: rewardItem.type,
                ...(rewardItem.useCount !== undefined && { remainingUseCount: rewardItem.useCount }),
                acquiredAt: new Date(),
                assignmentId: assignmentId,
                category: 'assignment',
                isLocked: false
              };

              await itemCardService.addStudentItemCardAtomic(studentId, studentItemCard);
              console.log(`🎁 아이템 보상 지급: ${rewardItem.name}`);
            }
          }
        }

        // rewardGrantedStudentIds 업데이트
        const updatedGrantedIds = [...(assignment.rewardGrantedStudentIds || []), studentId];
        await this.updateProjectAssignment(
          assignmentId,
          assignment.title,
          assignment.description,
          assignment.dueDate ? new Date(assignment.dueDate) : undefined,
          assignment.assignedStudentIds,
          undefined, // subTasks는 업데이트하지 않음
          assignment.color,
          assignment.isActive,
          assignment.rewardEnabled,
          assignment.rewardMessage,
          assignment.rewardCoins,
          assignment.rewardItemCardIds,
          updatedGrantedIds
        );

      } else if (!isProjectCompleted && alreadyGranted) {
        // 프로젝트 미완료: 보상 회수 (일반 과제와 동일한 방식)
        console.log(`⏳ 프로젝트 과제 미완료 보상 회수 시작: ${assignmentId} - ${studentId}`);

        // 코인 회수 (음수로 지급)
        if (hasCoins) {
          await coinService.grantCoinsToStudent(
            studentId,
            -assignment.rewardCoins!,
            `${assignment.title} 프로젝트 과제 보상 회수`,
            'General'
          );
          console.log(`💰 코인 회수: -${assignment.rewardCoins}코인`);
        }

        // 아이템 카드 잠금 (제거하지 않고 잠금 처리)
        if (hasItemCards) {
          await itemCardService.lockStudentItemCardsByAssignment(studentId, assignmentId);
          console.log(`🔒 아이템 카드 잠금 완료`);
        }

        // 캐시 무효화 - 보상 회수 즉시 반영
        try {
          const { userDataService } = await import('./userDataService');
          userDataService.invalidateMembersCache(this.userId, this.classId, this.memberGroup);
          console.log('🗑️ 프로젝트 과제 보상 회수 후 캐시 무효화 완료');
        } catch (error) {
          console.warn('캐시 무효화 실패 (무시):', error);
        }

        // rewardGrantedStudentIds에서 제거 (보상을 회수했으므로 다시 완료하면 보상을 재지급할 수 있도록 - 일반 과제와 통일)
        const updatedGrantedIds = (assignment.rewardGrantedStudentIds || []).filter(id => id !== studentId);
        await this.updateProjectAssignment(
          assignmentId,
          assignment.title,
          assignment.description,
          assignment.dueDate ? new Date(assignment.dueDate) : undefined,
          assignment.assignedStudentIds,
          undefined, // subTasks는 업데이트하지 않음
          assignment.color,
          assignment.isActive,
          assignment.rewardEnabled,
          assignment.rewardMessage,
          assignment.rewardCoins,
          assignment.rewardItemCardIds,
          updatedGrantedIds
        );
        console.log('✅ 프로젝트 과제 보상 회수 완료 및 기록 업데이트');
      } else if (isProjectCompleted && alreadyGranted) {
        // 이미 보상을 받았고 프로젝트가 완료 상태: 잠금 해제만 처리
        if (hasItemCards) {
          await itemCardService.unlockStudentItemCardsByAssignment(studentId, assignmentId);
          console.log(`🔓 아이템 카드 잠금 해제 완료`);
        }
      }

    } catch (error) {
      console.error('❌ 프로젝트 과제 보상 처리 실패:', error);
      // 보상 처리 실패해도 상태 업데이트는 완료되도록 에러를 throw하지 않음
    }
  }

  /**
   * 여러 멤버의 하위 과제 상태를 배치로 업데이트 (V2: 부분 업데이트 - dot notation)
   */
  async batchUpdateSubTaskStudentStatuses(
    assignmentId: string,
    subTaskId: string,
    studentUpdates: Array<{
      studentId: string;
      status: AssignmentStatusValue;
      points: number;
      memo?: string;
      submissionLink?: string;
      submissionMessage?: string;
      submittedAt?: Date;
      submissionImagePath?: string;
      submissionImageUpdatedAt?: string;
      submissionImageSizeBytes?: number;
    }>
  ): Promise<void> {
    try {
      console.log(`🚀 프로젝트 과제 하위 과제 배치 업데이트 시작 (V2): ${studentUpdates.length}명`);
      
      // V2: 중첩 객체 형식으로 문서 조회
      const consolidatedDocsV2 = await this.getAllConsolidatedDocumentsV2();
      
      let targetDocV2: ProjectAssignmentConsolidatedV2 | null = null;
      let targetAssignmentV2: ProjectAssignmentInfoV2 | null = null;
      let targetSubTaskV2: ProjectSubTaskInfoV2 | null = null;
      
      for (const docV2 of consolidatedDocsV2) {
        if (docV2.assignments[assignmentId]) {
          const assignment = docV2.assignments[assignmentId];
          if (assignment.subTasks[subTaskId]) {
            targetDocV2 = docV2;
            targetAssignmentV2 = assignment;
            targetSubTaskV2 = assignment.subTasks[subTaskId];
            break;
          }
        }
      }

      if (!targetDocV2 || !targetAssignmentV2 || !targetSubTaskV2) {
        throw new Error(`프로젝트 과제 ${assignmentId}의 하위 과제 ${subTaskId}를 찾을 수 없습니다.`);
      }

      // V2: 부분 업데이트 객체 생성 (dot notation)
      const updateObj: { [key: string]: any } = {
        [`assignments.${assignmentId}.updatedAt`]: new Date(),
        lastUpdated: serverTimestamp()
      };

      for (const update of studentUpdates) {
        const existingStatus = targetSubTaskV2.studentStatuses?.[update.studentId];
        
        const studentStatusV2: ProjectSubTaskStudentStatusV2 = {
          status: update.status,
          earnedPoints: update.points,
          lastUpdated: new Date()
        };
        
        // 제출 확인 데이터 처리
        if (Object.prototype.hasOwnProperty.call(update, 'submissionLink')) {
          if (update.submissionLink !== undefined) {
            studentStatusV2.submissionLink = update.submissionLink;
          }
        } else if (existingStatus?.submissionLink) {
          studentStatusV2.submissionLink = existingStatus.submissionLink;
        }
        
        if (Object.prototype.hasOwnProperty.call(update, 'submissionMessage')) {
          if (update.submissionMessage !== undefined) {
            studentStatusV2.submissionMessage = update.submissionMessage;
          }
        } else if (existingStatus?.submissionMessage) {
          studentStatusV2.submissionMessage = existingStatus.submissionMessage;
        }
        
        if (Object.prototype.hasOwnProperty.call(update, 'submittedAt')) {
          if (update.submittedAt !== undefined) {
            studentStatusV2.submittedAt = update.submittedAt;
          }
        } else if (existingStatus?.submittedAt) {
          studentStatusV2.submittedAt = existingStatus.submittedAt;
        }

        if (Object.prototype.hasOwnProperty.call(update, 'submissionImagePath')) {
          if (update.submissionImagePath !== undefined) {
            studentStatusV2.submissionImagePath = update.submissionImagePath;
          }
        } else if (existingStatus?.submissionImagePath) {
          studentStatusV2.submissionImagePath = existingStatus.submissionImagePath;
        }
        if (Object.prototype.hasOwnProperty.call(update, 'submissionImageUpdatedAt')) {
          if (update.submissionImageUpdatedAt !== undefined) {
            studentStatusV2.submissionImageUpdatedAt = update.submissionImageUpdatedAt;
          }
        } else if (existingStatus?.submissionImageUpdatedAt) {
          studentStatusV2.submissionImageUpdatedAt = existingStatus.submissionImageUpdatedAt;
        }
        if (Object.prototype.hasOwnProperty.call(update, 'submissionImageSizeBytes')) {
          if (update.submissionImageSizeBytes !== undefined) {
            studentStatusV2.submissionImageSizeBytes = update.submissionImageSizeBytes;
          }
        } else if (existingStatus?.submissionImageSizeBytes !== undefined) {
          studentStatusV2.submissionImageSizeBytes = existingStatus.submissionImageSizeBytes;
        }

        // V2: 각 학생별 부분 업데이트 경로 추가
        updateObj[`assignments.${assignmentId}.subTasks.${subTaskId}.studentStatuses.${update.studentId}`] = studentStatusV2;
      }

      // V2: 한 번의 updateDoc으로 모든 학생 상태 업데이트
      const docRef = doc(
        db, 
        'users', 
        this.userId, 
        'classes', 
        this.classId, 
        this.collectionName, 
        targetDocV2.id
      );
      
      await updateDoc(docRef, updateObj);

      // 쓰기 후 캐시 무효화 (모달/카드 재로딩 시 최신 데이터 반영)
      this.invalidateConsolidatedCache();

      console.log('✅ 프로젝트 과제 하위 과제 배치 업데이트 완료 (V2 부분 업데이트):', {
        assignmentId,
        subTaskId,
        studentCount: studentUpdates.length
      });

    } catch (error) {
      console.error('❌ 프로젝트 과제 하위 과제 배치 업데이트 실패:', error);
      throw error;
    }
  }

  /**
   * 모든 프로젝트 과제 조회
   */
  async loadAllProjectAssignments(): Promise<ProjectAssignmentInfo[]> {
    try {
      const consolidatedDocs = await this.getAllConsolidatedDocuments();
      
      const allAssignments: ProjectAssignmentInfo[] = [];
      // 문서 ID 순으로 순회해 flatten 순서 고정 (재조회 시 순서 변경 방지)
      const sortedDocs = [...consolidatedDocs].sort((d1, d2) => (d1.id || '').localeCompare(d2.id || ''));
      for (const doc of sortedDocs) {
        if (doc.assignments && Array.isArray(doc.assignments)) {
          const withCreatedAt = doc.assignments.map((a: any) => ({
            ...a,
            createdAt: a.createdAt ?? undefined
          }));
          // 같은 문서 내에서는 assignmentId 순으로 고정
          withCreatedAt.sort((a: any, b: any) => (a.assignmentId || '').localeCompare(b.assignmentId || ''));
          allAssignments.push(...withCreatedAt);
        }
      }

      // 반환 배열 전체를 생성일·assignmentId 기준으로 고정 정렬 (저장 후 재로딩 시 순서 유지)
      allAssignments.sort((a: any, b: any) => {
        const toMs = (x: any) => {
          if (x?.createdAt == null) return 0;
          const d = x.createdAt instanceof Date ? x.createdAt : (x.createdAt?.toDate?.() ?? new Date(0));
          return (d instanceof Date ? d : new Date(d)).getTime();
        };
        const byCreated = toMs(a) - toMs(b);
        if (byCreated !== 0) return byCreated;
        return (a.assignmentId || '').localeCompare(b.assignmentId || '');
      });

      console.log('✅ 모든 프로젝트 과제 조회 완료:', {
        documentCount: consolidatedDocs.length,
        assignmentCount: allAssignments.length
      });

      return allAssignments;
    } catch (error) {
      console.error('❌ 프로젝트 과제 조회 실패:', error);
      throw error;
    }
  }

  /**
   * 특정 프로젝트 과제 조회
   */
  async loadProjectAssignment(assignmentId: string): Promise<ProjectAssignmentInfo | null> {
    try {
      const allAssignments = await this.loadAllProjectAssignments();
      const assignment = allAssignments.find(a => a.assignmentId === assignmentId);
      return assignment || null;
    } catch (error) {
      console.error('❌ 프로젝트 과제 조회 실패:', error);
      throw error;
    }
  }

  /**
   * 프로젝트 과제 업데이트 (V2: 부분 업데이트 - dot notation)
   */
  async updateProjectAssignment(
    assignmentId: string,
    title?: string,
    description?: string,
    dueDate?: Date,
    assignedStudentIds?: string[],
    subTasks?: SubTask[],
    color?: string,
    isActive?: boolean,
    rewardEnabled?: boolean,
    rewardMessage?: string,
    rewardCoins?: number,
    rewardItemCardIds?: string[],
    rewardGrantedStudentIds?: string[]
  ): Promise<void> {
    try {
      // V2: 중첩 객체 형식으로 문서 조회
      const consolidatedDocsV2 = await this.getAllConsolidatedDocumentsV2();
      
      let targetDocV2: ProjectAssignmentConsolidatedV2 | null = null;
      let targetAssignmentV2: ProjectAssignmentInfoV2 | null = null;
      
      for (const docV2 of consolidatedDocsV2) {
        if (docV2.assignments[assignmentId]) {
          targetDocV2 = docV2;
          targetAssignmentV2 = docV2.assignments[assignmentId];
          break;
        }
      }

      if (!targetDocV2 || !targetAssignmentV2) {
        throw new Error(`프로젝트 과제 ${assignmentId}를 찾을 수 없습니다.`);
      }

      // V2: 부분 업데이트 객체 생성
      const updateObj: { [key: string]: any } = {
        [`assignments.${assignmentId}.updatedAt`]: new Date(),
        lastUpdated: serverTimestamp()
      };

      // 과제 기본 정보 업데이트
      if (title !== undefined) updateObj[`assignments.${assignmentId}.title`] = title;
      if (description !== undefined) updateObj[`assignments.${assignmentId}.description`] = description || '';
      if (dueDate !== undefined) updateObj[`assignments.${assignmentId}.dueDate`] = dueDate.toISOString().split('T')[0];
      if (assignedStudentIds !== undefined) updateObj[`assignments.${assignmentId}.assignedStudentIds`] = assignedStudentIds;
      if (color !== undefined) updateObj[`assignments.${assignmentId}.color`] = color;
      if (isActive !== undefined) updateObj[`assignments.${assignmentId}.isActive`] = isActive;

      // 보상 필드 처리
      if (rewardEnabled !== undefined) {
        updateObj[`assignments.${assignmentId}.rewardEnabled`] = rewardEnabled;
      }
      
      if (rewardEnabled === false) {
        // rewardEnabled가 false이면 보상 필드 삭제
        updateObj[`assignments.${assignmentId}.rewardMessage`] = deleteField();
        updateObj[`assignments.${assignmentId}.rewardCoins`] = deleteField();
        updateObj[`assignments.${assignmentId}.rewardItemCardIds`] = deleteField();
        // rewardGrantedStudentIds는 삭제하지 않음 (보상 지급 이력 유지)
      } else if (rewardEnabled === true || rewardEnabled === undefined) {
        if (rewardMessage !== undefined) {
          if (rewardMessage) updateObj[`assignments.${assignmentId}.rewardMessage`] = rewardMessage;
          else updateObj[`assignments.${assignmentId}.rewardMessage`] = deleteField();
        }
        if (rewardCoins !== undefined) {
          if (rewardCoins > 0) updateObj[`assignments.${assignmentId}.rewardCoins`] = rewardCoins;
          else updateObj[`assignments.${assignmentId}.rewardCoins`] = deleteField();
        }
        if (rewardItemCardIds !== undefined && rewardItemCardIds !== null) {
          if (rewardItemCardIds.length > 0) updateObj[`assignments.${assignmentId}.rewardItemCardIds`] = rewardItemCardIds;
          else updateObj[`assignments.${assignmentId}.rewardItemCardIds`] = deleteField();
        }
      }
      
      if (rewardGrantedStudentIds !== undefined) {
        if (rewardGrantedStudentIds.length > 0) updateObj[`assignments.${assignmentId}.rewardGrantedStudentIds`] = rewardGrantedStudentIds;
        else updateObj[`assignments.${assignmentId}.rewardGrantedStudentIds`] = deleteField();
      }

      // 멤버 배열 변경 처리 (assignedStudentIds 제공된 경우에만)
      if (assignedStudentIds !== undefined) {
        const previousStudentIds = targetAssignmentV2.assignedStudentIds || [];
        const newStudentIds = assignedStudentIds || [];
        
        // 제거된 멤버들의 상태 삭제 (모든 하위 과제에서)
        const removedStudentIds = previousStudentIds.filter(id => !newStudentIds.includes(id));
        const subTaskIds = Object.keys(targetAssignmentV2.subTasks || {});
        for (const subTaskId of subTaskIds) {
          for (const removedId of removedStudentIds) {
            updateObj[`assignments.${assignmentId}.subTasks.${subTaskId}.studentStatuses.${removedId}`] = deleteField();
          }
        }
        
        // 새로 추가된 멤버들의 초기 상태 생성 (모든 하위 과제에)
        const addedStudentIds = newStudentIds.filter(id => !previousStudentIds.includes(id));
        for (const subTaskId of subTaskIds) {
          for (const addedId of addedStudentIds) {
            updateObj[`assignments.${assignmentId}.subTasks.${subTaskId}.studentStatuses.${addedId}`] = {
              status: 'pending' as AssignmentStatusValue,
              earnedPoints: 0,
              lastUpdated: new Date()
            } as ProjectSubTaskStudentStatusV2;
          }
        }
      }

      // 하위 과제 정보 업데이트 (subTasks 제공된 경우에만)
      if (subTasks !== undefined) {
        const currentStudentIds = assignedStudentIds || targetAssignmentV2.assignedStudentIds;
        
        for (const subTask of subTasks) {
          const existingSubTask = targetAssignmentV2.subTasks?.[subTask.id];
          
          if (existingSubTask) {
            // 기존 하위 과제 업데이트 (멤버 상태는 유지)
            updateObj[`assignments.${assignmentId}.subTasks.${subTask.id}.subTaskOrder`] = subTask.order;
            updateObj[`assignments.${assignmentId}.subTasks.${subTask.id}.subTaskBasePoints`] = subTask.points;
            updateObj[`assignments.${assignmentId}.subTasks.${subTask.id}.subTaskTitle`] = subTask.title;
          } else {
            // 새 하위 과제 생성 (모든 부여받은 멤버의 초기 상태 생성)
            const initialStudentStatusesV2: { [studentId: string]: ProjectSubTaskStudentStatusV2 } = {};
            for (const studentId of currentStudentIds) {
              initialStudentStatusesV2[studentId] = {
                status: 'pending' as AssignmentStatusValue,
                earnedPoints: 0,
                lastUpdated: new Date()
              };
            }
            
            updateObj[`assignments.${assignmentId}.subTasks.${subTask.id}`] = {
              subTaskOrder: subTask.order,
              subTaskBasePoints: subTask.points,
              subTaskTitle: subTask.title,
              studentStatuses: initialStudentStatusesV2
            } as ProjectSubTaskInfoV2;
          }
        }
      }

      // V2: 한 번의 updateDoc으로 모든 필드 업데이트
      const docRef = doc(
        db, 
        'users', 
        this.userId, 
        'classes', 
        this.classId, 
        this.collectionName, 
        targetDocV2.id
      );
      
      await updateDoc(docRef, updateObj);
      this.invalidateConsolidatedCache();

      console.log('✅ 프로젝트 과제 통합 문서 업데이트 완료 (V2 부분 업데이트):', {
        assignmentId,
        documentId: targetDocV2.id,
        title,
        subTaskCount: subTasks?.length,
        studentCount: assignedStudentIds?.length
      });

    } catch (error) {
      console.error('❌ 프로젝트 과제 통합 문서 업데이트 실패:', error);
      throw error;
    }
  }

  /**
   * 프로젝트 과제 삭제 (V2: deleteField() 사용 - 해당 과제만 삭제)
   */
  async deleteProjectAssignment(assignmentId: string): Promise<void> {
    try {
      // V2: 중첩 객체 형식으로 문서 조회
      const consolidatedDocsV2 = await this.getAllConsolidatedDocumentsV2();
      
      let targetDocV2: ProjectAssignmentConsolidatedV2 | null = null;
      
      for (const docV2 of consolidatedDocsV2) {
        if (docV2.assignments[assignmentId]) {
          targetDocV2 = docV2;
          break;
        }
      }

      if (!targetDocV2) {
        throw new Error(`프로젝트 과제 ${assignmentId}를 찾을 수 없습니다.`);
      }

      // V2: deleteField()를 사용하여 해당 과제만 삭제
      const docRef = doc(
        db, 
        'users', 
        this.userId, 
        'classes', 
        this.classId, 
        this.collectionName, 
        targetDocV2.id
      );
      
      await updateDoc(docRef, {
        [`assignments.${assignmentId}`]: deleteField(),
        lastUpdated: serverTimestamp()
      });
      this.invalidateConsolidatedCache();

      console.log('✅ 프로젝트 과제 통합 문서에서 과제 삭제 완료 (V2):', {
        assignmentId,
        documentId: targetDocV2.id,
        remainingAssignments: Object.keys(targetDocV2.assignments).length - 1
      });

      try {
        await assignmentSubmissionImageService.deleteProjectAssignmentImages(
          this.userId,
          this.classId,
          this.memberGroup,
          assignmentId
        );
      } catch (storageError) {
        console.warn('프로젝트 과제 제출 이미지 Storage 삭제 실패 (무시):', storageError);
      }

    } catch (error) {
      console.error('❌ 프로젝트 과제 통합 문서에서 과제 삭제 실패:', error);
      throw error;
    }
  }

  /**
   * 문서 분할
   */
  private async splitDocument(
    targetDoc: ProjectAssignmentConsolidated,
    targetDocIndex: number
  ): Promise<void> {
    try {
      console.log('🔄 프로젝트 과제 문서 분할 시작:', {
        documentId: targetDoc.id,
        currentSize: targetDoc.totalSize,
        maxSize: this.maxDocumentSize
      });

      const midIndex = Math.ceil(targetDoc.assignments.length / 2);
      const firstHalf = targetDoc.assignments.slice(0, midIndex);
      const secondHalf = targetDoc.assignments.slice(midIndex);

      // 첫 번째 문서 업데이트
      const firstDoc: ProjectAssignmentConsolidated = {
        ...targetDoc,
        assignments: firstHalf,
        totalSize: this.calculateDocumentSize({ ...targetDoc, assignments: firstHalf })
      };

      const firstDocRef = doc(
        db, 
        'users', 
        this.userId, 
        'classes', 
        this.classId, 
        this.collectionName,
        firstDoc.id
      );
      
      await updateDoc(firstDocRef, {
        assignments: firstDoc.assignments,
        totalSize: firstDoc.totalSize,
        lastUpdated: serverTimestamp()
      });
      this.invalidateConsolidatedCache();

      // 두 번째 문서 생성
      const nextDocumentNumber = await this.getCurrentDocumentNumber() + 1;
      const secondDocId = await this.createNewConsolidatedDocument(nextDocumentNumber, {
        assignments: secondHalf,
        totalSize: this.calculateDocumentSize({ ...targetDoc, assignments: secondHalf })
      });

      console.log('✅ 프로젝트 과제 문서 분할 완료:', {
        firstDocument: firstDoc.id,
        secondDocument: secondDocId,
        firstHalfAssignments: firstHalf.length,
        secondHalfAssignments: secondHalf.length
      });

    } catch (error) {
      console.error('❌ 프로젝트 과제 문서 분할 실패:', error);
      throw error;
    }
  }
}
